import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Paper, 
  CircularProgress, 
  Card, 
  CardContent,
  TextField,
  Divider,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  FormControlLabel,
  Checkbox,
  GlobalStyles
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useClerk, useSignUp, useSignIn } from '@clerk/clerk-react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import SubscriptionFormDirect from '../components/subscription/SubscriptionFormDirect';
import { WorkspacePremium, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { isCaptchaError, getCaptchaErrorMessage } from "../utils/captchaUtils";

// API URL for the backend
const API_URL = process.env.REACT_APP_API_URL;
const planId = process.env.REACT_APP_STRIPE_PRICE_ID || "";
const planPrice = process.env.REACT_APP_STRIPE_PRICE_AMOUNT ? Number(process.env.REACT_APP_STRIPE_PRICE_AMOUNT) : 0;
// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

// Process steps
const steps = ['Account', 'Payment', 'Confirmation'];

export default function DirectProSignupPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Vista de login o registro
  const [viewMode, setViewMode] = useState<'register' | 'login'>('register');
  
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Clerk hooks
  const { signUp, isLoaded: isSignUpLoaded } = useSignUp();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const clerk = useClerk();

  // Get user state from Redux store to check if eligible for trial
  const storeUser = useSelector((state: RootState) => state.user.user);
  
  // Determine if user is eligible for trial period
  const isEligibleForTrial = !storeUser?.hadSubscriptionBefore;

  // Check for active session when component loads
  useEffect(() => {
    // Check if user already has an active session
    if (clerk.session) {
      console.log("Active session detected when loading component");
      
      // If there's an active session and we're at step 0, go directly to payment step
      if (activeStep === 0) {
        setActiveStep(1);
      }
    }
  }, [clerk.session, activeStep]);

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Validate the registration form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    if (confirmPassword !== password) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!agreedToTerms) {
      errors.terms = 'You must agree to the Terms of Service and Privacy Policy';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Validate login form
  const validateLoginForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Invalid email address';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Function to update the user name in our backend
  const updateUserNameInBackend = async (firstName: string, lastName: string) => {
    try {
      // Get authentication token from Clerk
      if (!clerk.session) {
        console.error("No active session found");
        return;
      }
      const token = await clerk.session.getToken();
      
      console.log('Updating user name in backend:', { firstName, lastName });
      
      // Call our backend API to update the user name
      const response = await fetch(`${API_URL}/users/update-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Clerk-User-Id': clerk.user?.id || '',
          'X-Clerk-Email': clerk.user?.primaryEmailAddress?.emailAddress || '',
          'X-Clerk-First-Name': firstName,
          'X-Clerk-Last-Name': lastName
        },
        body: JSON.stringify({ firstName, lastName })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update user name: ${response.statusText}`);
      }
      
      console.log('User name updated successfully in backend');
    } catch (error) {
      console.error('Error updating user name in backend:', error);
      // Don't throw error to prevent blocking the signup process
    }
  };

  // Handle account creation
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!signUp) {
      setError('System error: Unable to initialize the registration process.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create a new user with only email and password first
      await signUp.create({
        emailAddress: email,
        password,
      });

      // Now attempt to verify the email
      try {
        const signUpResponse = await signUp.prepareEmailAddressVerification({
          strategy: "email_code"
        });

        if (signUpResponse.status === 'complete') {
          // If email verification isn't required or is instant
          try {
            // Attempt to add the name after creating account
            // Clerk requirements: Make sure "Users can set their first and last name" 
            // is enabled in the Clerk Dashboard under Email, Phone, Username > Personal information
            await signUp.update({
              firstName,
              lastName,
            });
            
            // Call the backend to update the user name
            await updateUserNameInBackend(firstName, lastName);
          } catch (nameError) {
            console.error("Error setting name, but continuing:", nameError);
            // Continue even if setting the name fails
          }
          
          // Move to payment step
          setActiveStep(1);
        } else {
          // Show verification screen
          setActiveStep(0.5);
        }
      } catch (verifyError) {
        console.error("Error during email verification:", verifyError);
        
        // If verification fails but account was created, still move forward
        try {
          // Attempt to add the name even if verification fails
          await signUp.update({
            firstName,
            lastName,
          });
          
          // Call the backend to update the user name
          await updateUserNameInBackend(firstName, lastName);
        } catch (nameError) {
          console.error("Error setting name, but continuing:", nameError);
        }
        
        // Try to check if we have a session already
        if (clerk.session) {
          setActiveStep(1);
        } else {
          // Otherwise show verification screen
          setActiveStep(0.5);
        }
      }
    } catch (error: any) {
      console.error('Account creation error:', error);
      
      // Detectar el error específico de contraseña comprometida
      if (error.errors && error.errors.length > 0) {
        const pwnedPasswordError = error.errors.find(
          (err: any) => err.code === "form_password_pwned"
        );
        
        if (pwnedPasswordError) {
          setError('This password has been found in a data breach. Please use a different password for your security.');
          setFormErrors({
            ...formErrors,
            password: 'Please choose a more secure password',
            confirmPassword: 'Please choose a more secure password'
          });
        } else if (isCaptchaError(error)) {
          setError(getCaptchaErrorMessage(error));
        } else {
          setError('Error creating account. Please check your information and try again.');
        }
      } else if (isCaptchaError(error)) {
        setError(getCaptchaErrorMessage(error));
      } else {
        setError('Error creating account. Please check your information and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle user login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateLoginForm()) {
      return;
    }

    if (!signIn) {
      setError('System error: Unable to initialize the login process.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });
      
      if (result.status === "complete") {
        console.log("Login successful, setting active session");
        
        try {
          // Attempt to activate the session
          await clerk.setActive({ session: result.createdSessionId });
          
          console.log("Session activated successfully, ID:", result.createdSessionId);
          
          // Call the backend to update the user name
          await updateUserNameInBackend(firstName, lastName);
          
          // Wait a moment to ensure session is fully active before proceeding
          setTimeout(() => {
            // Now it's safe to proceed to payment step
            setLoading(false);
            setActiveStep(1);
          }, 500);
        } catch (sessionError) {
          console.error("Error activating session:", sessionError);
          setError("Error activating session. Please try again.");
          setLoading(false);
        }
      } else {
        console.log("Incomplete login state:", result.status);
        setError('Unable to sign in with those credentials.');
        setLoading(false);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Detectar el error específico de contraseña comprometida
      if (error.errors && error.errors.length > 0) {
        const pwnedPasswordError = error.errors.find(
          (err: any) => err.code === "form_password_pwned"
        );
        
        if (pwnedPasswordError) {
          setError('This password has been found in a data breach. Please use a different password for your security.');
          setFormErrors({
            ...formErrors,
            password: 'Please reset your password to a more secure one'
          });
        } else if (isCaptchaError(error)) {
          setError(getCaptchaErrorMessage(error));
        } else {
          setError('Error signing in. Please check your credentials and try again.');
        }
      } else if (isCaptchaError(error)) {
        setError(getCaptchaErrorMessage(error));
      } else {
        setError('Error signing in. Please check your credentials and try again.');
      }
      
      setLoading(false);
    }
  };

  // Verification step state
  const [verificationCode, setVerificationCode] = useState('');
  
  // Handle email verification code submit
  const handleSubmitCode = () => {
    if (verificationCode.length === 6 && !loading) {
      handleVerifyEmail(verificationCode);
    }
  };
  
  // Handle keypress event for verification code field
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6 && !loading) {
      handleSubmitCode();
    }
  };
  
  // Resend verification code
  const handleResendCode = async () => {
    setLoading(true);
    try {
      if (signUp) {
        await signUp.prepareEmailAddressVerification({
          strategy: "email_code"
        });
        setError("A new verification code has been sent to your email.");
      }
    } catch (error) {
      console.error("Error resending code:", error);
      setError("Error sending new verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (verificationCode: string) => {
    if (!signUp) {
      // If there's no signup object but the user has a session, they're likely already logged in
      if (clerk.session) {
        console.log("No signup object but found active session, proceeding to payment step");
        
        // Try to update user info if we have a session
        try {
          await clerk.user?.update({
            firstName,
            lastName
          });
          
          // Update the user's name in our backend
          await updateUserNameInBackend(firstName, lastName);
        } catch (error) {
          console.error("Error updating user information:", error);
          // Continue even if updating fails
        }
        
        setActiveStep(1);
        return;
      }
      
      setError('System error: Unable to verify email.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log(`Attempting to verify email with code: ${verificationCode}`);
      
      // First, try to update the name before verification if needed
      try {
        await signUp.update({
          firstName,
          lastName
        });
        
        // Update the user's name in our backend
        await updateUserNameInBackend(firstName, lastName);
      } catch (nameError) {
        console.error("Error setting name during verification, but continuing:", nameError);
        // Continue even if name update fails
      }
      
      // Now verify the email address
      const response = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      console.log('Verification response:', response);
      
      if (response.status === 'complete') {
        // If sign up was completed, set the session to active
        if (response.createdSessionId) {
          try {
            await clerk.setActive({ session: response.createdSessionId });
            
            // Update the user's name in our backend
            await updateUserNameInBackend(firstName, lastName);
          } catch (sessionError) {
            console.error("Error activating session:", sessionError);
            // Continue anyway - we'll try to detect the session below
          }
        }
        
        // Navigate directly to payment step after verification
        setActiveStep(1);
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      
      // Check if the error is because the signup might no longer exist
      // or the user is already verified/logged in
      if (error.message && (
          error.message.includes('already verified') || 
          error.message.includes('already active') ||
          error.message.includes('session exists') ||
          error.message.includes('No sign up')
        )) {
        console.log('Session may be active already or signup no longer exists. Checking for active session.');
        
        // If we have an active session, continue to payment step
        if (clerk.session) {
          console.log("Found active session, proceeding to payment step");
          
          // Try to update user info if we have a session
          try {
            await clerk.user?.update({
              firstName,
              lastName
            });
            
            // Update the user's name in our backend
            await updateUserNameInBackend(firstName, lastName);
          } catch (error) {
            console.error("Error updating user information:", error);
            // Continue even if updating fails
          }
          
          setActiveStep(1);
        } else {
          // If we don't have a session, try to redirect to login or restart
          console.log("No active session found, suggest restarting flow");
          setError('Verification session expired. Please refresh the page and try again.');
        }
      } else {
        setError('Error verifying email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle between login and signup views
  const toggleViewMode = () => {
    setViewMode(viewMode === 'register' ? 'login' : 'register');
    setError(null);
    setFormErrors({});
    
    // Ensure activeStep remains at 0 to show the correct form
    if (activeStep !== 0) {
      setActiveStep(0);
    }
  };

  // Handle subscription completion
  const handleSubscriptionSuccess = () => {
    setActiveStep(2);
    
    // Ensure user session is active and redirect immediately
    try {
      // Refresh the clerk session to ensure it's fully active
      if (clerk.session) {
        // Set active session again to ensure it's properly activated
        try {
          clerk.setActive({ session: clerk.session.id });
          console.log("Reactivated session with ID:", clerk.session.id);
        } catch (error) {
          console.error("Error reactivating session:", error);
        }
        
        // Log for debugging
        console.log("Session is active with ID:", clerk.session.id);
        
        // Wait for the session to be properly registered
        setTimeout(() => {
          // Force a full page navigation to ensure clean state
          console.log("Redirecting to dashboard with active session");
          window.location.href = '/dashboard';
        }, 1500); // Increased timeout to ensure session is properly set
      } else {
        console.error("No active session found after subscription. Forcing navigation.");
        // Fallback if no session is found - try to navigate to dashboard anyway
        // A page reload may be necessary for the session to be properly detected
        window.location.href = '/dashboard?refresh=true';
      }
    } catch (error) {
      console.error("Error during dashboard redirect:", error);
      // Fallback if there's an error - force navigation
      window.location.href = '/dashboard?refresh=true';
    }
  };

  // Cancel and return to main page
  const handleCancel = () => {
    navigate('/');
  };

  // Render current step
  const renderStep = () => {
    if (!isSignUpLoaded || !isSignInLoaded) {
      return <CircularProgress />;
    }

    if (activeStep === 0) {
      // Determine which view to show: register or login
      if (viewMode === 'register') {
        return (
          <Box component="form" onSubmit={handleCreateAccount}>
            <Typography variant="h5" gutterBottom fontWeight="600">
              Create Your Pro Account
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Complete your information below to get started with unlimited transcriptions.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
              </Box>
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                variant="outlined"
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Terms and Conditions Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link component={RouterLink} to="/terms-of-service" target="_blank" color="primary">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link component={RouterLink} to="/privacy-policy" target="_blank" color="primary">
                    Privacy Policy
                  </Link>
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            {formErrors.terms && (
              <Typography color="error" variant="caption" sx={{ mt: -1, mb: 2, display: 'block' }}>
                {formErrors.terms}
              </Typography>
            )}

            {/* CAPTCHA Widget - Required for Clerk's bot protection */}
            <div 
              id="clerk-captcha" 
              data-cl-theme="auto"
              data-cl-size="normal"
              data-cl-language="auto"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: "#5D5FEF",
                color: "#fff",
                py: 1.5,
                mb: 2,
                '&:hover': {
                  backgroundColor: "#4A4BD5",
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue to Payment'}
            </Button>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="textSecondary">
                OR
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Link 
                component="button" 
                variant="body2" 
                onClick={toggleViewMode}
                sx={{ color: '#5D5FEF', textDecoration: 'none', fontWeight: 500 }}
              >
                I already have an account
              </Link>
            </Box>
          </Box>
        );
      } else {
        // Login view
        return (
          <Box component="form" onSubmit={handleLogin}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={toggleViewMode}
                sx={{ mr: 1, color: '#5D5FEF' }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight="600">
                Sign In to Upgrade
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Log in to your account to upgrade to Pro and enjoy unlimited transcriptions.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* CAPTCHA Widget - Required for Clerk's bot protection on login */}
            <div 
              id="clerk-captcha" 
              data-cl-theme="auto"
              data-cl-size="normal"
              data-cl-language="auto"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                backgroundColor: "#5D5FEF",
                color: "#fff",
                py: 1.5,
                mb: 2,
                '&:hover': {
                  backgroundColor: "#4A4BD5",
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Continue to Payment'}
            </Button>
          </Box>
        );
      }
    } else if (activeStep === 0.5) {
      // Email verification step
      return (
        <Box>
          <Typography variant="h5" gutterBottom fontWeight="600">
            Verify Your Email
          </Typography>
          <Typography variant="body1" paragraph>
            We've sent a verification code to {email}. Please enter it below to continue.
          </Typography>
          
          {error && (
            <Alert 
              severity={error.includes('has been sent') ? 'success' : 'error'} 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Verification Code"
              variant="outlined"
              sx={{ mb: 2 }}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter 6-digit code"
              inputProps={{ 
                maxLength: 6, 
                inputMode: 'numeric',
                pattern: '[0-9]*' 
              }}
              autoFocus
            />
            
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmitCode}
              disabled={loading || verificationCode.length !== 6}
              sx={{
                backgroundColor: "#5D5FEF",
                color: "#fff",
                py: 1.5,
                mb: 2,
                '&:hover': {
                  backgroundColor: "#4A4BD5",
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
            </Button>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Didn't receive a code? Check your spam folder or:
            </Typography>
            <Button 
              variant="text" 
              onClick={handleResendCode}
              disabled={loading}
              sx={{ color: '#5D5FEF' }}
            >
              Resend Verification Code
            </Button>
          </Box>
        </Box>
      );
    } else if (activeStep === 1) {
      // Ensure we have an active session before showing the payment form
      if (!clerk.session) {
        console.log("No active session detected in payment step, attempting to set active session");
        
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Preparing payment form...
            </Typography>
            <CircularProgress sx={{ mt: 2 }} />
          </Box>
        );
      }
        
      return (
        <Box>
          <Typography variant="h5" gutterBottom fontWeight="600">
            Complete Your Subscription
          </Typography>
          
          {/* Show user info to confirm who is subscribing */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Subscribing as: <strong>{clerk.user?.firstName} {clerk.user?.lastName}</strong> ({clerk.user?.primaryEmailAddress?.emailAddress})
            </Typography>
          </Box>
          
          <Elements stripe={stripePromise}>
            <SubscriptionFormDirect
              onCancel={handleCancel}
              onSuccess={handleSubscriptionSuccess}
              planId={planId}
              planPrice={planPrice}
              trialPeriodDays={14}
              isDirectSignup={true}
            />
          </Elements>
        </Box>
      );
    } else if (activeStep === 2) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="600">
            Subscription Completed!
          </Typography>
          <Typography variant="body1" paragraph>
            Congratulations! You now have access to all Pro features and unlimited transcriptions.
          </Typography>
          <Typography variant="body1">
            You'll be redirected to the dashboard in a few seconds...
          </Typography>
          <CircularProgress sx={{ mt: 3 }} />
        </Box>
      );
    } else {
      return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "100vh", 
        backgroundColor: "#f4f6f8",
      }}
    >
      <GlobalStyles 
        styles={{
          '#clerk-captcha:empty': {
            display: 'none !important',
            margin: '0 !important',
            padding: '0 !important'
          }
        }}
      />
      <Header />

      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
        {/* Onboarding Welcome Card */}
        <Card 
          sx={{ 
            mb: 4, 
            background: 'linear-gradient(135deg, #5D5FEF 0%, #4A4BD5 100%)',
            color: 'white',
            boxShadow: '0px 8px 20px rgba(93, 95, 239, 0.25)',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <WorkspacePremium sx={{ fontSize: 40, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">Welcome to Tranzcribe Pro</Typography>
            </Box>
            <Typography variant="body1">
              Thank you for choosing Tranzcribe Pro! With your new subscription, you'll enjoy unlimited transcriptions, advanced AI summaries, and priority support.
              {isEligibleForTrial && " Your 14-day free trial starts today."}
            </Typography>
          </CardContent>
        </Card>

        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 4 }, 
            borderRadius: 2,
            backgroundColor: activeStep === 1 ? '#1a1a2e' : '#fff',
            color: activeStep === 1 ? '#fff' : 'inherit'
          }}
        >
          <Stepper 
            activeStep={Math.floor(activeStep)} 
            sx={{ 
              mb: 4,
              "& .MuiStepIcon-root": {
                color: "rgba(93, 95, 239, 0.5)"
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: "#5D5FEF"
              },
              "& .MuiStepLabel-label": {
                color: activeStep === 1 ? 'rgba(255, 255, 255, 0.7)' : 'inherit'
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: activeStep === 1 ? '#FFFFFF' : '#5D5FEF'
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && activeStep !== 0 && viewMode === 'login' && (
            <Typography color="error" sx={{ mb: 3 }}>
              {error}
            </Typography>
          )}

          {renderStep()}
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
} 